import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { useMarketListQuery } from "Market/Api/Query/useMarketListQuery";
import { useMarketRegionList } from "Market/Api/Query/useMarketRegionList";
import { useMarket } from "Market/Providers/CurrentMarketProvider";
import { useCreateOrderManual } from "Order/Api/Mutation/createOrderManualMutation";
import { OrderPaths } from "Order/Routes/Routing";
import { useVerticalListQuery } from "Settings/Modules/OrderSetup/Api/Query/useVerticalListQuery";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Grid2 } from "@mui/material";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import {
	getOrderCreateManualFormSchema,
	type OrderCreateManualFormModel,
} from "./Schema/orderCreateManualSchema";

const orderResource = Resources.base.order.shared;
const orderCreateForm = Resources.base.order.createForm;

export const CreateManualForm = () => {
	const { t } = useResource();
	const navigate = useNavigate();
	const { market } = useMarket();

	const form = useForm<OrderCreateManualFormModel>({
		resolver: zodResolver(getOrderCreateManualFormSchema()),
		defaultValues: {
			address: "",
			customer: {
				name: "",
				email: "",
				phone: "",
			},
		},
	});

	const {
		watch,
		control,
		setValue,
		formState: { errors },
	} = form;

	const selectedMarketId = watch("marketId");

	const { data: marketList } = useMarketListQuery();
	const {
		data: marketRegionList,
		refetch: refetchMarketRegions,
		isLoading: isLoadingMarketRegions,
	} = useMarketRegionList(selectedMarketId);

	const { data: verticals } = useVerticalListQuery();

	const { handleServerError } = useHandleServerError(form);
	const { mutate: createManulOrder, isPending: createNewOrderPending } =
		useCreateOrderManual();

	const handleSubmitCreate = () => {
		handleServerError(createManulOrder, {
			onSuccess: (order) => {
				navigate(OrderPaths.Detail.path(order.id));
			},
		});
	};

	useEffect(() => {
		if (market && marketList) {
			setValue("marketId", market.id);
		}
	}, [market, marketList]);

	useEffect(() => {
		setValue("marketRegionId", null);
		selectedMarketId && refetchMarketRegions();
	}, [selectedMarketId]);

	return (
		<form>
			<Grid2 container spacing={2}>
				<Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
					<Select
						id="marketId"
						label={t(orderCreateForm.market)}
						fullWidth
						options={
							marketList?.map((m) => {
								return {
									label: m.name,
									value: m.id,
								};
							}) ?? []
						}
						variant="outlined"
						name="marketId"
						control={control}
						errors={errors}
						numeric
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
					<Select
						id="marketRegionId"
						label={t(orderCreateForm.marketRegion)}
						fullWidth
						options={
							marketRegionList?.map((mr) => {
								return {
									label: mr.name,
									value: mr.id,
								};
							}) ?? []
						}
						variant="outlined"
						name="marketRegionId"
						control={control}
						errors={errors}
						disabled={!selectedMarketId || isLoadingMarketRegions}
						isLoading={isLoadingMarketRegions}
						numeric
					/>
				</Grid2>
				<Grid2 size={{ xs: 12 }}>
					<Input
						id="address"
						name="address"
						label={t(orderCreateForm.address)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
				</Grid2>
				<Grid2 size={{ xs: 12 }}>
					<Select
						id="verticalId"
						name="verticalId"
						label={t(orderCreateForm.vertical)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						numeric
						options={
							verticals?.map((v) => {
								return {
									label: v.name,
									value: v.id,
								};
							}) ?? []
						}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="customer.name"
						name="customer.name"
						label={t(orderCreateForm.customerName)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="customer.email"
						name="customer.email"
						label={t(orderCreateForm.customerEmail)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="customer.phone"
						name="customer.phone"
						label={t(orderCreateForm.customerPhone)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
				</Grid2>
				<Grid2 size={{ xs: 12 }}>
					<Button
						variant="contained"
						color="primary"
						onClick={handleSubmitCreate}
						loading={createNewOrderPending}
					>
						{t(orderResource.create)}
					</Button>
				</Grid2>
			</Grid2>
		</form>
	);
};
